import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { BookOpen, Target, ClipboardList, Trophy, Users, User as UserIcon, Search, Loader2, Save, CheckCircle2 } from 'lucide-react';

// FASE QUIZZES · PASO 3 — Modal "Crear Quiz" abierto desde un Study concreto.
// Usa endpoints existentes (POST /quizzes, POST /quizzes/{qid}/assign,
// GET /classrooms/invitees, GET /groups/{gid}/students). El snapshot inmutable
// se genera en el backend al crear el Quiz (Paso 1B). No hay lógica de
// snapshots en el frontend.

const QUIZ_TYPES = [
  { key:'practice',    label:'Práctica',        icon: Target,         color:'from-indigo-500 to-blue-500' },
  { key:'evaluation',  label:'Evaluación',      icon: ClipboardList,  color:'from-amber-500 to-orange-500' },
  { key:'module_exam', label:'Examen de módulo',icon: Trophy,         color:'from-emerald-500 to-teal-500' },
];

const LEVEL_LABEL = { beginner:'Básico', intermediate:'Intermedio', advanced:'Avanzado' };

function suggestTitle(study, quizType) {
  const suffix = quizType === 'practice' ? 'Práctica' : quizType === 'module_exam' ? 'Examen' : 'Evaluación';
  return `${study?.name || 'Study'} — ${suffix}`;
}
function suggestIntro(study) {
  const n = (study?.name || 'este tema').toLowerCase();
  return `Pon a prueba todo lo que has aprendido sobre ${n}.`;
}

export default function CreateQuizDialog({ open, onClose, study, onCreated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'school_admin';
  const availableQuizTypes = isAdmin ? QUIZ_TYPES : QUIZ_TYPES.filter(t => t.key !== 'module_exam');
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [quizType, setQuizType] = useState('practice');
  const [selectMode, setSelectMode] = useState('all'); // 'all' | 'subset'
  const [selectedActivityIds, setSelectedActivityIds] = useState([]);
  const [maxPoints, setMaxPoints] = useState(30);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [allowContinue, setAllowContinue] = useState(true);

  const [invitees, setInvitees] = useState({ students: [], groups: [] });
  const [loadingInvitees, setLoadingInvitees] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset al abrir
  useEffect(() => {
    if (!open || !study) return;
    setTitle(suggestTitle(study, 'practice'));
    setIntro(suggestIntro(study));
    setQuizType('practice');
    setSelectMode('all');
    setSelectedActivityIds((study.activities || []).map(a => a.id));
    setMaxPoints(30); setMaxAttempts(3); setAllowContinue(true);
    setSelectedGroupIds([]); setSelectedStudentIds([]); setStudentSearch('');
  }, [open, study?.id]); // eslint-disable-line

  // Cargar invitees al abrir
  useEffect(() => {
    if (!open) return;
    setLoadingInvitees(true);
    api.get('/classrooms/invitees')
      .then(r => setInvitees({ students: r.data?.students || [], groups: r.data?.groups || [] }))
      .catch(() => setInvitees({ students: [], groups: [] }))
      .finally(() => setLoadingInvitees(false));
  }, [open]);

  // Sugerencia dinámica del título cuando cambia el tipo (sólo si no fue editado)
  const [titleEdited, setTitleEdited] = useState(false);
  useEffect(() => {
    if (!titleEdited && study) setTitle(suggestTitle(study, quizType));
  }, [quizType, titleEdited, study]);

  // Orden original del Study preservado siempre
  const orderedActivities = useMemo(() => (study?.activities || []), [study]);
  const activityIdsInStudyOrder = useMemo(() => orderedActivities.map(a => a.id), [orderedActivities]);

  const finalActivityIds = useMemo(() => {
    if (selectMode === 'all') return activityIdsInStudyOrder;
    const set = new Set(selectedActivityIds);
    return activityIdsInStudyOrder.filter(id => set.has(id));
  }, [selectMode, activityIdsInStudyOrder, selectedActivityIds]);

  const toggleActivity = (aid) => {
    setSelectedActivityIds(s => s.includes(aid) ? s.filter(x => x !== aid) : [...s, aid]);
  };

  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return invitees.students;
    return invitees.students.filter(s => (s.name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q));
  }, [invitees.students, studentSearch]);

  const toggleGroup = (gid) => setSelectedGroupIds(s => s.includes(gid) ? s.filter(x => x !== gid) : [...s, gid]);
  const toggleStudent = (sid) => setSelectedStudentIds(s => s.includes(sid) ? s.filter(x => x !== sid) : [...s, sid]);

  // Resumen: total alumnos únicos incluyendo los que vienen por grupos
  const [groupStudentPreview, setGroupStudentPreview] = useState({}); // gid -> [user_id]
  useEffect(() => {
    // Cargar student_ids de los grupos seleccionados para poder mostrar el conteo unificado.
    // Reutiliza GET /groups/{gid}/students (permitido a teacher).
    const missing = selectedGroupIds.filter(g => !groupStudentPreview[g]);
    if (missing.length === 0) return;
    Promise.all(missing.map(gid => api.get(`/groups/${gid}/students`).then(r => [gid, r.data]).catch(() => [gid, []])))
      .then(pairs => {
        setGroupStudentPreview(prev => {
          const next = { ...prev };
          pairs.forEach(([gid, arr]) => { next[gid] = (arr || []).map(s => s.user_id).filter(Boolean); });
          return next;
        });
      });
  }, [selectedGroupIds]); // eslint-disable-line

  const targetStudentIds = useMemo(() => {
    const set = new Set(selectedStudentIds);
    selectedGroupIds.forEach(gid => (groupStudentPreview[gid] || []).forEach(uid => set.add(uid)));
    return Array.from(set);
  }, [selectedStudentIds, selectedGroupIds, groupStudentPreview]);

  const submit = async (mode /* 'draft' | 'create_and_assign' */) => {
    if (!title.trim()) { toast.error('El título es obligatorio'); return; }
    if (finalActivityIds.length === 0) { toast.error('Selecciona al menos una actividad'); return; }
    const status = mode === 'draft' ? 'draft' : 'published';
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        study_id: study.id,
        activity_ids: selectMode === 'all' ? null : finalActivityIds,
        module_slot: (study?.module && !isNaN(Number(study.module))) ? Number(study.module) : null,
        quiz_type: quizType,
        intro_text: intro,
        max_points_per_activity: Number(maxPoints) || 30,
        max_attempts: Number(maxAttempts) || 3,
        allow_continue: !!allowContinue,
        status,
      };
      const { data: quiz } = await api.post('/quizzes', payload);
      if (mode === 'create_and_assign') {
        if (targetStudentIds.length === 0) {
          toast('Quiz creado. Selecciona destinatarios para asignarlo.', { description: 'Guardado como publicado sin asignaciones.' });
        } else {
          const { data: asg } = await api.post(`/quizzes/${quiz.id}/assign`, { student_ids: targetStudentIds });
          const created = (asg?.created || []).length;
          const dupes = (asg?.already_assigned || []).length;
          toast.success(`Quiz creado y asignado`, { description: `${created} asignación(es) nueva(s)${dupes ? ` · ${dupes} ya existente(s)` : ''}.` });
        }
      } else {
        toast.success('Borrador guardado', { description: 'Puedes asignarlo más tarde.' });
      }
      onCreated?.(quiz);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear el Quiz');
    } finally {
      setBusy(false);
    }
  };

  if (!study) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="create-quiz-dialog" className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle data-testid="cq-title" className="flex items-center gap-2 text-2xl font-extrabold">
            <Target className="w-6 h-6 text-indigo-600"/> Crear Quiz
          </DialogTitle>
          <DialogDescription>Configura el Quiz y asígnalo a tus alumnos o grupos.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 overflow-y-auto pr-1 pb-1">
          {/* 1. Study de origen */}
          <div data-testid="cq-source-study" className="rounded-xl bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 p-3">
            <div className="text-[10px] uppercase tracking-widest text-indigo-600 font-bold flex items-center gap-1"><BookOpen className="w-3 h-3"/> Study de origen</div>
            <div data-testid="cq-source-study-name" className="font-display font-extrabold text-lg text-slate-900 truncate">{study.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {study.module ? <>Módulo {study.module}</> : 'Sin módulo'} · Nivel <b>{LEVEL_LABEL[study.level] || study.level || '—'}</b> · {orderedActivities.length} actividades
            </div>
          </div>

          {/* 2. Título + Intro */}
          <div className="space-y-2">
            <div>
              <Label htmlFor="cq-title-input">Título del Quiz</Label>
              <Input id="cq-title-input" data-testid="cq-title-input" value={title} onChange={e => { setTitle(e.target.value); setTitleEdited(true); }}/>
            </div>
            <div>
              <Label htmlFor="cq-intro">Introducción</Label>
              <Textarea id="cq-intro" data-testid="cq-intro" rows={2} value={intro} onChange={e => setIntro(e.target.value)}/>
              <div className="text-[11px] text-slate-500 mt-0.5">Este texto se muestra al alumno antes de comenzar. Las instrucciones de cada actividad vienen del Study.</div>
            </div>
          </div>

          {/* 3. Tipo */}
          <div>
            <Label>Tipo de Quiz</Label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="cq-type-picker">
              {availableQuizTypes.map(t => {
                const active = quizType === t.key;
                return (
                  <button key={t.key} type="button" data-testid={`cq-type-${t.key}`} onClick={() => setQuizType(t.key)}
                    className={`text-left rounded-xl border p-2.5 transition-all ${active ? 'border-indigo-500 ring-2 ring-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white hover:border-indigo-300'}`}>
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${t.color} text-white grid place-items-center mb-1`}><t.icon className="w-4 h-4"/></div>
                    <div className="text-sm font-semibold text-slate-800">{t.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 4. Actividades */}
          <div>
            <Label>Actividades</Label>
            <div className="flex items-center gap-4 mt-1">
              <label className="inline-flex items-center gap-2 text-sm">
                <input data-testid="cq-mode-all" type="radio" name="cq-mode" checked={selectMode==='all'} onChange={() => setSelectMode('all')}/>
                Todas las actividades ({orderedActivities.length})
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input data-testid="cq-mode-subset" type="radio" name="cq-mode" checked={selectMode==='subset'} onChange={() => setSelectMode('subset')}/>
                Seleccionar actividades
              </label>
            </div>
            {selectMode === 'subset' && (
              <div data-testid="cq-activity-list" className="mt-2 max-h-60 overflow-y-auto rounded-lg border border-slate-200 divide-y">
                {orderedActivities.map((a, i) => {
                  const checked = selectedActivityIds.includes(a.id);
                  return (
                    <label key={a.id} data-testid={`cq-activity-${a.id}`} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                      <Checkbox checked={checked} onCheckedChange={() => toggleActivity(a.id)}/>
                      <span className="text-xs text-slate-400 w-6 shrink-0">#{i+1}</span>
                      <span className="flex-1 truncate">{a.title || '(sin título)'}</span>
                      <span className="text-[10px] uppercase text-slate-400">{a.type}</span>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="text-[11px] text-slate-500 mt-1">El orden final siempre respeta el orden del Study.</div>
          </div>

          {/* 5. Configuración */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Puntos por actividad</Label>
              <Input data-testid="cq-max-points" type="number" min={1} max={999} value={maxPoints} onChange={e => setMaxPoints(e.target.value)}/>
            </div>
            <div>
              <Label>Intentos máximos</Label>
              <Input data-testid="cq-max-attempts" type="number" min={1} max={10} value={maxAttempts} onChange={e => setMaxAttempts(e.target.value)}/>
            </div>
            <div>
              <Label>Continuar después</Label>
              <label className="mt-2 flex items-center gap-2 text-sm">
                <Checkbox data-testid="cq-allow-continue" checked={allowContinue} onCheckedChange={v => setAllowContinue(!!v)}/>
                Sí, permitir
              </label>
            </div>
          </div>

          {/* 6. Asignación */}
          <div className="rounded-xl border border-slate-200 p-3 bg-white">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-slate-500"/>
              <span className="font-semibold text-sm">Asignar Quiz</span>
              {loadingInvitees && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400"/>}
            </div>
            {/* Grupos */}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-1">Grupos</div>
              {invitees.groups.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No tienes grupos disponibles.</div>
              ) : (
                <div data-testid="cq-groups-list" className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                  {invitees.groups.map(g => {
                    const checked = selectedGroupIds.includes(g.id);
                    return (
                      <label key={g.id} data-testid={`cq-group-${g.id}`} className={`flex items-center gap-2 px-2 py-1.5 rounded border text-xs cursor-pointer ${checked ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                        <Checkbox checked={checked} onCheckedChange={() => toggleGroup(g.id)}/>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{g.name || g.code || 'Grupo'}</div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {g.code ? `${g.code} · ` : ''}{g.module_slot ? `Módulo ${g.module_slot} · ` : ''}{g.student_count ?? 0} alumno{(g.student_count ?? 0)===1?'':'s'}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Alumnos individuales */}
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-1">Alumnos individuales</div>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
                <Input data-testid="cq-student-search" className="pl-7 h-8 text-xs" placeholder="Buscar por nombre o email…" value={studentSearch} onChange={e => setStudentSearch(e.target.value)}/>
              </div>
              <div data-testid="cq-students-list" className="mt-1.5 max-h-40 overflow-y-auto rounded border border-slate-200 divide-y">
                {filteredStudents.length === 0 ? (
                  <div className="text-xs text-slate-400 italic px-3 py-2">Sin alumnos disponibles.</div>
                ) : (
                  filteredStudents.slice(0, 60).map(s => {
                    const checked = selectedStudentIds.includes(s.id);
                    return (
                      <label key={s.id} data-testid={`cq-student-${s.id}`} className="flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                        <Checkbox checked={checked} onCheckedChange={() => toggleStudent(s.id)}/>
                        <UserIcon className="w-3 h-3 text-slate-400"/>
                        <span className="flex-1 truncate">{s.name}</span>
                        <span className="text-[10px] text-slate-400 truncate max-w-[45%]">{s.email}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* 7. Resumen */}
          <div data-testid="cq-summary" className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm">
            <div className="font-semibold text-emerald-800 flex items-center gap-1 mb-1"><CheckCircle2 className="w-4 h-4"/> Resumen</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-slate-700">
              <div><b>Título:</b> {title || '—'}</div>
              <div><b>Study:</b> {study.name}</div>
              <div><b>Actividades:</b> <span data-testid="cq-summary-acts">{finalActivityIds.length}</span> de {orderedActivities.length}</div>
              <div><b>Tipo:</b> {availableQuizTypes.find(t=>t.key===quizType)?.label}</div>
              <div><b>Puntos:</b> {maxPoints} por actividad</div>
              <div><b>Intentos:</b> {maxAttempts}</div>
              <div><b>Continuar:</b> {allowContinue ? 'Sí' : 'No'}</div>
              <div><b>Destinatarios:</b> <span data-testid="cq-summary-recipients">{targetStudentIds.length}</span> alumno{targetStudentIds.length===1?'':'s'}</div>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-3 border-t">
          <Button data-testid="cq-cancel" variant="outline" onClick={() => onClose?.()} disabled={busy}>Cancelar</Button>
          <Button data-testid="cq-save-draft" variant="outline" onClick={() => submit('draft')} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Save className="w-4 h-4 mr-1"/>} Guardar borrador
          </Button>
          <Button data-testid="cq-create-assign" onClick={() => submit('create_and_assign')} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Target className="w-4 h-4 mr-1"/>} Crear y asignar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
